import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Warehouse, ArrowRight, CheckCircle2, Clock, Globe, Shield, 
  Package, Zap, Phone, Mail, Box, Users, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';

const WarehousingPage = () => {
  const { t } = useTranslation();

  // Structured data for Warehousing service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Warehousing Coordination",
    "description": "Storage solutions coordinated through third-party warehouse operators.",
    "provider": {
      "@type": "Organization",
      "name": "Elementary Cargo B.V.",
      "url": "https://elementarycargo.com"
    },
    "serviceType": "Warehousing",
    "areaServed": "Netherlands"
  };

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Warehousing", path: "/services/warehousing" }
  ];

  const features = [
    { 
      id: 'single-contact',
      icon: Users, 
      title: t('warehousingPage.features.singleContact'), 
      description: t('warehousingPage.features.singleContactDesc')
    },
    { 
      id: 'partner-capabilities',
      icon: Package, 
      title: t('warehousingPage.features.partnerCapabilities'), 
      description: t('warehousingPage.features.partnerCapabilitiesDesc')
    },
    { 
      id: 'visibility',
      icon: Shield, 
      title: t('warehousingPage.features.visibility'), 
      description: t('warehousingPage.features.visibilityDesc')
    },
    { 
      id: 'integrated',
      icon: Globe, 
      title: t('warehousingPage.features.integrated'), 
      description: t('warehousingPage.features.integratedDesc')
    }
  ];

  const services = [
    {
      id: 'short-term',
      title: t('warehousingPage.services.shortTerm'),
      description: t('warehousingPage.services.shortTermDesc'),
      features: t('warehousingPage.services.shortTermFeatures', { returnObjects: true })
    },
    {
      id: 'long-term',
      title: t('warehousingPage.services.longTerm'),
      description: t('warehousingPage.services.longTermDesc'),
      features: t('warehousingPage.services.longTermFeatures', { returnObjects: true })
    },
    {
      id: 'pick-pack',
      title: t('warehousingPage.services.pickPack'),
      description: t('warehousingPage.services.pickPackDesc'),
      features: t('warehousingPage.services.pickPackFeatures', { returnObjects: true })
    },
    {
      id: 'value-added',
      title: t('warehousingPage.services.valueAdded'),
      description: t('warehousingPage.services.valueAddedDesc'),
      features: t('warehousingPage.services.valueAddedFeatures', { returnObjects: true })
    }
  ];

  const cargoTypes = t('warehousingPage.cargoTypesList', { returnObjects: true });
  const integrationFeatures = t('warehousingPage.integrationFeatures', { returnObjects: true });
  const whatWeDo = t('warehousingPage.whatWeDoList', { returnObjects: true });
  const whatWeDontDo = t('warehousingPage.whatWeDoNotList', { returnObjects: true });

  return (
    <div className="min-h-screen bg-offwhite" data-testid="warehousing-page">
      <SEO 
        titleKey="seo.warehousing.title"
        descriptionKey="seo.warehousing.description"
        path="/services/warehousing"
        structuredData={serviceSchema}
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1553413077-190dd305871c?w=1920&q=80"
            alt="Warehouse operations - coordinated through our partner operators"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/80" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gold/20 rounded-xl flex items-center justify-center">
                <Warehouse className="w-7 h-7 text-gold" />
              </div>
              <span className="text-gold text-sm font-semibold tracking-wider uppercase">{t('warehousingPage.badge')}</span>
            </div>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('warehousingPage.heroTitle')}
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed mb-8">
              {t('warehousingPage.heroSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/quote" data-testid="warehousing-quote-btn">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all">
                  {t('warehousingPage.requestQuote')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg">
                  {t('warehousingPage.talkWithUs')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('warehousingPage.whyCoordinateWithUs')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900">
              {t('warehousingPage.structuredCoordination')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-xl bg-navy-900/5 flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-space text-lg font-bold text-navy-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('warehousingPage.whatWeCoordinate')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('warehousingPage.warehousingOptions')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('warehousingPage.warehousingOptionsDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 transition-colors">
                  <CardContent className="p-8">
                    <h3 className="font-space text-xl font-bold text-navy-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {Array.isArray(service.features) && service.features.map((feature, idx) => (
                        <li key={`${service.id}-feature-${idx}`} className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cargo Types */}
      <section className="py-20 bg-navy-900">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                {t('warehousingPage.whatWeCoordinateStorageFor')}
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-6">
                {t('warehousingPage.cargoTypes')}
              </h2>
              <p className="text-platinum-light mb-8">
                {t('warehousingPage.cargoTypesDesc')}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {Array.isArray(cargoTypes) && cargoTypes.map((type, index) => (
                  <div key={`cargo-type-${index}`} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                    <span className="text-sm">{type}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80"
                  alt="Warehouse storage coordinated through partner operators"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrated with Shipping */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-navy-900/5 flex items-center justify-center mx-auto mb-6">
              <Globe className="w-8 h-8 text-gold" />
            </div>
            <h2 className="font-space text-3xl font-bold text-navy-900 mb-4">
              {t('warehousingPage.coordinatedWithShipping')}
            </h2>
            <p className="text-slate-600 mb-8">
              {t('warehousingPage.coordinatedWithShippingDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {Array.isArray(integrationFeatures) && integrationFeatures.map((feature, index) => (
                <div key={`integration-${index}`} className="bg-offwhite px-6 py-3 rounded-xl flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-gold" />
                  <span className="text-slate-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-20 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('warehousingPage.ourRole')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('warehousingPage.clearAboutWhatWeDo')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('warehousingPage.transparencyDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-green-200 bg-green-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('warehousingPage.whatWeDo')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDo) && whatWeDo.map((item, index) => (
                      <li key={`what-we-do-${index}`} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-slate-200 bg-slate-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center">
                      <X className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('warehousingPage.whatWeDoNot')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDontDo) && whatWeDontDo.map((item, index) => (
                      <li key={`what-we-dont-${index}`} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-4">
              {t('warehousingPage.needCoordination')}
            </h2>
            <p className="text-platinum-light text-lg mb-8">
              {t('warehousingPage.needCoordinationDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all w-full sm:w-auto">
                  {t('warehousingPage.requestAQuote')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 text-platinum-light text-sm">
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-4 h-4" /> info@elementarycargo.com
              </a>
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-4 h-4" /> +31 62 538 1824
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WarehousingPage;
